/** MarginItem — how vertical spacing is actually decided.
 *
 * `MarginItemDecoration` is a RecyclerView ItemDecoration that zeroes the rect and then
 * sets only the resources an item declares. Two consequences the CSS has to respect:
 *
 *  1. these are OUTER OFFSETS AND DO NOT COLLAPSE — two adjacent 8dp items give 16dp;
 *  2. only items implementing MarginItem get any spacing at all. Across the four
 *     screens that is CardActivityItem, SectionItem and BodyItem. Everything else is
 *     flush, with its spacing baked into its own layout.
 *
 * Spec §2.0.
 */
export interface Margins {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export const NO_MARGINS: Margins = { top: 0, bottom: 0, left: 0, right: 0 };

/** CardActivityItem.Style controls only the horizontal insets. */
export const CARD_STYLE_INSET = { SINGLE: 16, SLIDER: 8, NESTED: 4 } as const;

export const SECTION_MARGINS: Margins = { top: 16, bottom: 8, left: 16, right: 16 };
export const BODY_MARGINS: Margins = { top: 8, bottom: 16, left: 16, right: 16 };

export const cardMargins = (style: keyof typeof CARD_STYLE_INSET): Margins => ({
  top: style === 'SINGLE' ? 8 : 0,
  bottom: style === 'SINGLE' ? 8 : 0,
  left: CARD_STYLE_INSET[style],
  right: CARD_STYLE_INSET[style],
});

/** Margins as a style object. Flex `gap` would collapse them; these must not. */
export const marginStyle = (m: Margins) => ({
  marginTop: m.top,
  marginBottom: m.bottom,
  marginLeft: m.left,
  marginRight: m.right,
});
